import CodeConfigBlock from '../CodeConfigBlock';
import { Share2, Package, ShoppingCart, Layers, KeyRound } from 'lucide-react';

const formatPrice = (p) => `${parseFloat(p).toFixed(2).replace('.', ',')} €`;

// Onglet « Acheter des codes » — l'offre affichée suit la méthode choisie en haut (codesSubTab).
// Toute la config (durée, groupe, cible, mode) reste dans le parent : les modales de checkout la consomment.
export default function BuyPacksTab({
    codesSubTab, onGoToCodes,
    // thématique
    pricing, currentPricing, selectedDuree, setSelectedDuree, themLabel, setThemLabel, onBuyPack,
    // catégorie
    catList, catSelectedId, setCatSelectedId, catMode, setCatMode, catLabel, setCatLabel, catCodeOffers, onBuyCategory,
    // mono
    qcSousSujets, qcSelectedSs, setQcSelectedSs, qcMode, setQcMode, qcLabel, setQcLabel, quizCodeOffers, onBuyQuiz,
}) {
    return (
        <div className="share-panel__buy">
            {/* === Thematique mode === */}
            {codesSubTab === 'thematique' && pricing && (
                <>
                    <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginBottom: 12 }}>
                        Codes d'acc&egrave;s &agrave; <strong>toute la th&eacute;matique</strong>. Le participant cr&eacute;e son propre compte et peut faire tous les quiz (sans pouvoir modifier le contenu).
                    </p>
                    <CodeConfigBlock
                        groupValue={themLabel}
                        onGroupChange={setThemLabel}
                        durationOptions={[
                            { value: '1m', label: '1 mois' },
                            { value: '6m', label: '6 mois' },
                            { value: '12m', label: '12 mois' },
                        ]}
                        durationValue={selectedDuree}
                        onDurationChange={setSelectedDuree}
                    />
                    <div className="share-panel__pack-grid" style={{ marginTop: 10 }}>
                        {Object.entries(currentPricing).map(([slug, pack], i, arr) => {
                            const unitPrice = (pack.price / pack.nb).toFixed(2);
                            const popular = arr.length > 1 && i === Math.floor(arr.length / 2);
                            return (
                                <div key={slug} className={`share-panel__pack-card share-panel__pack-card--readonly${popular ? ' share-panel__pack-card--popular' : ''}`}>
                                    {popular && <span className="share-panel__pack-badge">POPULAIRE</span>}
                                    <Package size={20} />
                                    <div className="share-panel__pack-nb">{pack.nb} codes</div>
                                    <div className="share-panel__pack-price">{formatPrice(pack.price)}</div>
                                    <div className="share-panel__pack-unit">{unitPrice.replace('.', ',')} €/code</div>
                                </div>
                            );
                        })}
                    </div>
                    <button className="btn btn-primary sub-modal-cta" onClick={onBuyPack}>
                        <ShoppingCart size={14} /> Acheter des codes th&eacute;matique
                    </button>
                </>
            )}

            {/* === Category mode === */}
            {codesSubTab === 'category' && (
                <>
                    <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginBottom: 12 }}>
                        Codes d'acc&egrave;s &agrave; <strong>tous les sujets d'une cat&eacute;gorie</strong>. Le participant n'a pas besoin de cr&eacute;er un compte. 1 code = 1 participant. Id&eacute;al d&egrave;s 2 sujets dans la cat&eacute;gorie.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <CodeConfigBlock
                            targetLabel="Catégorie"
                            targetPlaceholder="— Choisir une catégorie —"
                            targetOptions={catList}
                            targetValue={catSelectedId}
                            onTargetChange={setCatSelectedId}
                            groupValue={catLabel}
                            onGroupChange={setCatLabel}
                            mode={catMode}
                            onModeChange={setCatMode}
                            modeDescriptions={{
                                quiz: 'Résultats à la fin de chaque quiz',
                                apprentissage: 'Correction après chaque réponse',
                                sealed: 'Résultats masqués — à desceller après validation',
                            }}
                        />
                        <div className="share-panel__pack-grid">
                            {catCodeOffers.map((o, i, arr) => {
                                const popular = arr.length > 1 && i === Math.floor(arr.length / 2);
                                return (
                                <div key={o.id} className={`share-panel__pack-card share-panel__pack-card--readonly${popular ? ' share-panel__pack-card--popular' : ''}`}>
                                    {popular && <span className="share-panel__pack-badge">POPULAIRE</span>}
                                    <Layers size={20} />
                                    <div className="share-panel__pack-nb">{o.nb_codes} codes</div>
                                    <div className="share-panel__pack-price">
                                        {o.price_eur}&euro;
                                        <span className="share-panel__pack-unit-inline">({(o.price_eur / o.nb_codes * 100).toFixed(0)}c/code)</span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        <button className="btn btn-primary sub-modal-cta"
                            disabled={!catSelectedId}
                            onClick={onBuyCategory}>
                            <ShoppingCart size={14} /> Acheter des codes cat&eacute;gorie
                        </button>
                        <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', textAlign: 'center' }}>
                            Lien d'acc&egrave;s : <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: 3 }}>{window.location.origin}/category-code</code>
                        </p>
                    </div>
                </>
            )}

            {/* === Mono quiz mode === */}
            {codesSubTab === 'mono' && (
                <>
                    <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginBottom: 12 }}>
                        Codes d'acc&egrave;s &agrave; <strong>un seul sujet</strong>. Le participant n'a pas besoin de cr&eacute;er un compte. Id&eacute;al pour les formations et &eacute;valuations.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <CodeConfigBlock
                            targetLabel="Sujet"
                            targetPlaceholder="— Choisir un sujet —"
                            targetOptions={qcSousSujets.map(ss => ({ id: ss.sous_sujet_id || ss.id, label: ss.sous_sujet_label || ss.label }))}
                            targetValue={qcSelectedSs}
                            onTargetChange={setQcSelectedSs}
                            groupValue={qcLabel}
                            onGroupChange={setQcLabel}
                            mode={qcMode}
                            onModeChange={setQcMode}
                            modeDescriptions={{
                                quiz: 'Résultats à la fin du quiz',
                                apprentissage: 'Correction après chaque réponse',
                                sealed: 'Résultats masqués — à desceller après validation',
                            }}
                        />
                        <div className="share-panel__pack-grid">
                            {quizCodeOffers.map((o, i, arr) => {
                                const popular = arr.length > 1 && i === Math.floor(arr.length / 2);
                                return (
                                <div key={o.id} className={`share-panel__pack-card share-panel__pack-card--readonly${popular ? ' share-panel__pack-card--popular' : ''}`}>
                                    {popular && <span className="share-panel__pack-badge">POPULAIRE</span>}
                                    <KeyRound size={20} />
                                    <div className="share-panel__pack-nb">{o.nb_codes} codes</div>
                                    <div className="share-panel__pack-price">
                                        {o.price_eur}&euro;
                                        <span className="share-panel__pack-unit-inline">({(o.price_eur / o.nb_codes * 100).toFixed(0)}c/code)</span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        <button className="btn btn-primary sub-modal-cta"
                            disabled={!qcSelectedSs}
                            onClick={onBuyQuiz}>
                            <ShoppingCart size={14} /> Acheter des codes quiz
                        </button>
                        <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', textAlign: 'center' }}>
                            Lien d'acc&egrave;s : <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: 3 }}>{window.location.origin}/quiz-code</code>
                        </p>
                    </div>
                </>
            )}

            {/* === Libre (entraînement libre) — gratuit, rien à acheter === */}
            {codesSubTab === 'open' && (
                <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-muted)' }}>
                    <Share2 size={26} style={{ color: 'var(--primary)' }} />
                    <p style={{ fontSize: '0.88em', lineHeight: 1.6, maxWidth: 460, margin: '12px auto 16px' }}>
                        L'<strong>entra&icirc;nement libre</strong> est <strong>gratuit et illimit&eacute;</strong> &mdash; il n'y a rien &agrave; acheter.
                        G&eacute;n&egrave;re un lien public depuis l'onglet <strong>Mes codes</strong> et colle-le o&ugrave; tu veux.
                    </p>
                    <button className="btn btn-primary btn-sm" onClick={onGoToCodes}>
                        <Share2 size={14} /> G&eacute;n&eacute;rer un lien libre
                    </button>
                </div>
            )}
        </div>
    );
}
